import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { SmartToyOutlined } from '@mui/icons-material';
import { Avatar, Box, Stack } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';

import useGetRobotName from 'hooks/use-get-robot-name';
import useDefaultCellRenderer from 'pages/Bulletin/hooks/use-default-cell-renderer';
import ProtectedData, { isProtectedData } from 'pages/components/ProtectedData';
import formatDate from 'utils/format-date';
import Utils from 'utils/Utils';

interface Related {
    [type: string]: {
        id: string;
        type: string;
        title: string;
        published: string;
        viewCount: number;
        reporter: string;
        bulletinFamily: string;
    };
}

export default function useRelatedTableData(related: Related) {
    const renderCell = useDefaultCellRenderer();
    const getRobotName = useGetRobotName();

    const data = useMemo(
        () =>
            Object.values(related ?? {})
                .flat()
                .map((item) => ({
                    id: item.id,
                    type: item.type,
                    bulletinFamily: item.bulletinFamily,
                    viewCount: item.viewCount,
                    title: item.title,
                    published: item.published
                })),
        [related]
    );

    const columns = useMemo<ColumnDef<(typeof data)[number]>[]>(
        () => [
            {
                header: 'Reporter',
                size: 170,
                accessorFn: ({ type }) => type,
                cell: function Reporter(info) {
                    const type = info.getValue<string>();
                    const to = isProtectedData(type) ? null : `/search?query=type:${type}`;

                    return (
                        <Stack
                            direction={'row'}
                            gap={'8px'}
                            alignItems={'center'}
                            sx={{ textDecoration: 'none', color: 'inherit' }}
                            {...(to ? { component: Link, to, target: '_blank' } : {})}
                        >
                            <ProtectedData data={type} rounded>
                                {({ isProtected, content }) => (
                                    <Avatar
                                        {...(isProtected
                                            ? { children: <SmartToyOutlined color="action" /> }
                                            : { src: Utils.getImage(content) })}
                                        sx={{ width: '24px', height: '24px' }}
                                    />
                                )}
                            </ProtectedData>
                            {<ProtectedData data={type}>{getRobotName(type)}</ProtectedData>}
                        </Stack>
                    );
                }
            },
            {
                header: 'Title',
                minSize: 100,
                accessorFn: ({ title }) => title,
                cell: function Title(info) {
                    const title = info.getValue<string>();
                    const { id, type } = info.row.original;
                    const href = isProtectedData(type) || isProtectedData(id) ? null : `/${type}/${id}`;
                    return isProtectedData(title) ? (
                        <ProtectedData data={title} />
                    ) : href ? (
                        <Box
                            component={Link}
                            to={href}
                            target="_blank"
                            rel="noopener nofollow"
                            sx={{ textDecoration: 'none', color: 'inherit' }}
                        >
                            {info.renderValue<string>()}
                        </Box>
                    ) : (
                        info.renderValue()
                    );
                }
            },
            {
                header: 'Published',
                size: 120,
                accessorFn: ({ published }) => published,
                cell: function Published(info) {
                    const value = info.getValue<string>();

                    if (value) {
                        return (
                            <Stack>
                                <ProtectedData data={value}>
                                    {({ isProtected }) => (
                                        <Box component={'span'} suppressHydrationWarning>
                                            {isProtected ? 'd MMM yyyy' : formatDate(value, 'd MMM yyyy')}
                                        </Box>
                                    )}
                                </ProtectedData>
                                <ProtectedData data={value}>
                                    {({ isProtected }) => (
                                        <Box component={'span'} sx={{ color: '#AFAFAF' }} suppressHydrationWarning>
                                            {isProtected ? 'HH:mm' : formatDate(value, 'HH:mm')}
                                        </Box>
                                    )}
                                </ProtectedData>
                            </Stack>
                        );
                    }
                    return info.renderValue();
                }
            },
            {
                header: 'Views',
                size: 120,
                accessorFn: ({ viewCount }) => viewCount,
                cell: renderCell
            },
            {
                header: 'Family',
                size: 190,
                enableColumnFilter: true,
                filterFn: (row, id, filter) => {
                    const value = row.getValue<unknown[]>(id);
                    return filter.length > 0 && value.length > 0 && filter.some((item) => value === item);
                },
                meta: {
                    options: [...new Set(data.map(({ bulletinFamily }) => bulletinFamily))]
                },
                accessorFn: ({ bulletinFamily }) => bulletinFamily,
                cell: renderCell
            }
        ],
        [data, getRobotName, renderCell]
    );

    return { data, columns };
}
